import { ActionTree } from 'vuex';
import { IRootState } from '@/store/types';
import { ISurvey, ReviewData } from './types';
import SET_SURVEY from './graphql/setSurvey';
import GET_SURVEY from './graphql/getSurvey';
import CLEAR_SURVEY from './graphql/clearSurvey';
import { setReviewSurvey, getReviewSurvey, removeAllLocalReviewSurvey } from '@/utils/local';
import { getMapKey } from './mutations';

const actions: ActionTree<ISurvey, IRootState> = {
  async setData({ commit, state, rootState }, reviewData: ReviewData) {
    // 拉取所有失败 同步远端
    let localReviewDatas: Array<ReviewData> = [];
    try {
      // 过滤失败
      state.reviewDataFlatMap.forEach((review_data) => {
        if (!review_data.isUpload)
          localReviewDatas.push({
            ...review_data,
            isUpload: true
          });
      });

      localReviewDatas.push({
        ...reviewData,
        isUpload: true
      });

      if (!rootState.common.authingId || rootState.common.environment?.kind !== 'public-preview') {
        throw Error('未登录状态');
      }

      // 过滤changeHook
      localReviewDatas = localReviewDatas.map((item) => {
        const { changeHook, ...remoteData } = item;
        return remoteData;
      });

      const courseId = rootState.common.environment.sharedKey;
      await window.$getClemanClient().apollo.mutate({
        mutation: SET_SURVEY,
        variables: {
          survey: {
            courseVersion: rootState.common.version,
            courseId,
            answers: localReviewDatas.map((data) => {
              const { isUpload, ...reviewData } = data;
              return {
                ...reviewData,
                content: JSON.stringify(data.content),
                step: String(reviewData.step) || ''
              };
            })
          }
        }
      });
      commit('updateSurveyDatas', localReviewDatas);
      // 上传成功清除本地
      removeAllLocalReviewSurvey(getCourseId(rootState));
    } catch (error) {
      commit(
        'updateSurveyDatas',
        localReviewDatas.map((item) => {
          return {
            ...item,
            isUpload: false
          };
        })
      );
      // pub环境不进行本地保存
      if (rootState.common.environment?.kind === 'public-preview') {
        // 保存至本地

        const localData = JSON.stringify([...filterHookByMap(state.reviewDataFlatMap)]);
        setReviewSurvey(rootState.common.version, getCourseId(rootState), localData);
      }
    }
  },

  async initData({ commit, rootState }) {
    const reviewDataStr = getReviewSurvey(rootState.common.version, getCourseId(rootState)) || '';
    // 从远端拿数据
    try {
      if (!rootState.common.authingId || rootState.common.environment?.kind !== 'public-preview') {
        throw Error('未登录状态');
      }
      const data = await window.$getClemanClient().apollo.query({
        query: GET_SURVEY,
        variables: {
          courseId: rootState.common.environment.sharedKey
        }
      });
      const surveyDatas = (data.data.course.learningAnswers as Array<ReviewData>).map((item) => {
        return {
          ...item,
          content: JSON.parse(item.content as string),
          step: item.step && Number(item.step),
          isUpload: true
        };
      });
      // 将接口的数据先行填充
      commit('updateSurveyDatas', surveyDatas);

      // 补充本地isUpload失败的数据进行覆盖
      const courseId =
        rootState.common.environment?.kind === 'public-preview'
          ? rootState.common.environment.sharedKey
          : '';
      const reviewDataFlatMap = new Map(JSON.parse(reviewDataStr)) as Map<string, ReviewData>;
      // 过滤isUpload
      const reviewDatas: Array<ReviewData> = [];
      reviewDataFlatMap.forEach((reviewData) => {
        if (!reviewData.isUpload) reviewDatas.push(reviewData);
      });
      // 更新填充
      commit('updateSurveyDatas', reviewDatas);
    } catch (error) {
      // 失败将全量拿本地数据
      try {
        commit('updateSurveyDataMap', new Map(JSON.parse(reviewDataStr)));
      } catch (error) {}
    }
  },

  async clearData({ commit, rootState }) {
    // 清除远端
    if (rootState.common.authingId && rootState.common.environment?.kind === 'public-preview') {
      // 特殊情况没有清除后边会进行覆盖更新
      await window.$getClemanClient().apollo.mutate({
        mutation: CLEAR_SURVEY,
        variables: {
          courseVersion: rootState.common.version,
          courseId: rootState.common.environment.sharedKey
        }
      });
    }
    // 清除本地
    removeAllLocalReviewSurvey(getCourseId(rootState));
    // 清除store
    commit('clearSurveyDatas');
  },

  getReviewData({ commit, state }, reviewData: ReviewData) {
    const returnData = (() => {
      if (!reviewData.contentId) return;
      const markData = state.reviewDataMap.get(reviewData.contentId);
      if (!markData) return;
      if (!(markData instanceof Map)) return [markData];
      // 包含mark标记
      if (reviewData.mark) {
        const stepData = markData.get(reviewData.mark);
        if (!(stepData instanceof Map)) return [stepData];
        if (!reviewData.step) return [...stepData.values()];
        return [stepData.get(reviewData.step)];
      }
      // 包含step标记
      if (reviewData.step) {
        const stepDataArr = [...markData.values()];
        return stepDataArr
          .filter((stepData) => {
            return stepData instanceof Map;
          })
          .map((stepMap) => {
            return (stepMap as Map<string, ReviewData>).get(reviewData.step);
          })
          .filter(Boolean);
      }
      const pathDatas: Array<ReviewData> = []
      state.reviewDataFlatMap.forEach((value, key) => {
        if (key.startsWith(`contentId=${reviewData.contentId}&`)) {
          pathDatas.push(value)
        }
      })
      return pathDatas;
    })();
    // 注册数据改变回调
    returnData?.forEach((item) => {
      item &&
        commit('setChangeHook', {
          ...item,
          changeHook: reviewData.changeHook
        });
    });
    return returnData;
  },
  // 入参为批量查询
  async getReviewDatas({ state, dispatch }, reviewDatas: Array<ReviewData>) {
    const returnData = []
    for (const reviewData of reviewDatas) {
      const data = await dispatch('getReviewData', reviewData)
      if (data) returnData.push(...data)
    }
    return returnData
  }
};



const getCourseId = (rootState: IRootState) => {
  return rootState.common.environment?.kind === 'public-preview'
    ? rootState.common.environment.sharedKey
    : '';
};

const filterHookByMap = (reviewDataFlatMap: Map<string, ReviewData>) => {
  const filterMap = new Map();
  reviewDataFlatMap.forEach((data, key) => {
    const { changeHook, ...remoteData } = data;
    filterMap.set(key, remoteData);
  });
  return filterMap;
};
export default actions;
